import * as React from 'react';
import { createPortal } from 'react-dom';

import { cn } from './lib/cn';

export type ToastVariant = 'success' | 'info' | 'warning' | 'danger';

export type ToastAction = {
  readonly label: string;
  readonly onClick: () => void;
};

export type ToastOptions = {
  readonly description?: string;
  readonly action?: ToastAction;
  readonly duration?: number | null;
};

type ToastInternal = {
  readonly id: number;
  readonly variant: ToastVariant;
  readonly title: string;
  readonly description?: string;
  readonly action?: ToastAction;
  readonly duration: number | null;
};

type ToastApi = {
  show: (variant: ToastVariant, title: string, options?: ToastOptions) => number;
  success: (title: string, options?: ToastOptions) => number;
  info: (title: string, options?: ToastOptions) => number;
  warning: (title: string, options?: ToastOptions) => number;
  danger: (title: string, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
};

const ToastContext = React.createContext<ToastApi | null>(null);

const DEFAULT_DURATION: Record<ToastVariant, number | null> = {
  success: 5000,
  info: 5000,
  warning: 8000,
  danger: null,
};

const trackClass: Record<ToastVariant, string> = {
  success: 'bg-success-100',
  info: 'bg-info-400/20',
  warning: 'bg-warning-100',
  danger: 'bg-danger-100',
};

const fillClass: Record<ToastVariant, string> = {
  success: 'bg-success-500',
  info: 'bg-info-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
};

const iconClass: Record<ToastVariant, string> = {
  success: 'text-success-600',
  info: 'text-info-500',
  warning: 'text-warning-500',
  danger: 'text-danger-600',
};

export function ToastProvider({
  children,
  closeLabel = 'Dismiss',
}: {
  readonly children?: React.ReactNode;
  readonly closeLabel?: string;
}) {
  const [toasts, setToasts] = React.useState<readonly ToastInternal[]>([]);
  const idRef = React.useRef(0);

  const dismiss = React.useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = React.useCallback((variant: ToastVariant, title: string, options?: ToastOptions): number => {
    idRef.current += 1;
    const id = idRef.current;
    const duration = options?.duration === undefined ? DEFAULT_DURATION[variant] : options.duration;
    const next: ToastInternal = {
      id,
      variant,
      title,
      duration,
      ...(options?.description !== undefined ? { description: options.description } : {}),
      ...(options?.action !== undefined ? { action: options.action } : {}),
    };
    setToasts((current) => [...current, next]);
    return id;
  }, []);

  const api = React.useMemo<ToastApi>(
    () => ({
      show,
      success: (title, options) => show('success', title, options),
      info: (title, options) => show('info', title, options),
      warning: (title, options) => show('warning', title, options),
      danger: (title, options) => show('danger', title, options),
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} closeLabel={closeLabel} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

function ToastViewport({
  toasts,
  onDismiss,
  closeLabel,
}: {
  readonly toasts: readonly ToastInternal[];
  readonly onDismiss: (id: number) => void;
  readonly closeLabel: string;
}) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 px-4 pb-6 sm:inset-x-auto sm:bottom-auto sm:right-6 sm:top-6 sm:items-end sm:px-0"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} closeLabel={closeLabel} />
      ))}
    </div>,
    document.body,
  );
}

function ToastItem({
  toast,
  onDismiss,
  closeLabel,
}: {
  readonly toast: ToastInternal;
  readonly onDismiss: () => void;
  readonly closeLabel: string;
}) {
  const { variant, title, description, action, duration } = toast;
  const isUrgent = variant === 'warning' || variant === 'danger';

  React.useEffect(() => {
    if (duration === null) return;
    const id = window.setTimeout(onDismiss, duration);
    return () => window.clearTimeout(id);
  }, [duration, onDismiss]);

  return (
    <div
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
      className={cn(
        'pointer-events-auto relative w-full max-w-[360px] overflow-hidden',
        'rounded-lg bg-surface shadow-lg ring-1 ring-border',
        'animate-in fade-in slide-in-from-right-4 duration-slow ease-out',
      )}
    >
      <div className="flex items-start gap-3 px-4 pb-3 pt-3">
        <span className={cn('mt-0.5 flex-none', iconClass[variant])} aria-hidden>
          <VariantIcon variant={variant} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <p className="text-base font-normal leading-snug text-text-primary">{title}</p>
            <button
              type="button"
              onClick={onDismiss}
              aria-label={closeLabel}
              className="-mr-1 -mt-1 inline-flex h-6 w-6 flex-none items-center justify-center rounded-sm text-text-tertiary transition-colors duration-micro ease-out hover:bg-hover-bg hover:text-text-primary focus-visible:outline-none focus-visible:shadow-focus"
            >
              <CloseIcon />
            </button>
          </div>
          {description !== undefined && (
            <p className="mt-1 whitespace-pre-line text-base-sm text-text-tertiary">{description}</p>
          )}
          {action !== undefined && (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  action.onClick();
                  onDismiss();
                }}
                className={cn(
                  'inline-flex h-control-sm items-center justify-center rounded-md border px-3 text-base font-medium',
                  'transition-colors duration-micro ease-out',
                  'focus-visible:outline-none focus-visible:shadow-focus',
                  variant === 'danger'
                    ? 'border-danger-500 bg-danger-500 text-interactive-on hover:bg-danger-600'
                    : 'border-border bg-surface text-text-primary hover:bg-hover-bg',
                )}
              >
                {action.label}
              </button>
            </div>
          )}
        </div>
      </div>
      {duration !== null && (
        <div className={cn('h-1 w-full', trackClass[variant])}>
          <div
            className={cn('h-full', fillClass[variant])}
            style={{
              animation: `immo-toast-progress ${duration}ms linear forwards`,
            }}
          />
        </div>
      )}
      {duration === null && <div className={cn('h-1 w-full', fillClass[variant])} />}
      <style>{progressKeyframes}</style>
    </div>
  );
}

const progressKeyframes = `@keyframes immo-toast-progress { from { width: 100%; } to { width: 0%; } }`;

function VariantIcon({ variant }: { readonly variant: ToastVariant }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  if (variant === 'success') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12 2.5 2.5L15.5 10" />
      </svg>
    );
  }
  if (variant === 'info') {
    return (
      <svg {...common}>
        <path d="M9 18h6" />
        <path d="M10 22h4" />
        <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2V18h6v-1.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2Z" />
      </svg>
    );
  }
  if (variant === 'warning') {
    return (
      <svg {...common}>
        <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="9" />
      <path d="m5.6 5.6 12.8 12.8" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
      aria-hidden
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="6" y1="18" x2="18" y2="6" />
    </svg>
  );
}
